/**
 * Shared device identity for Hub tables.
 * Always prefer hostname/name + currently logged-on user over raw UUID.
 */

export type DeviceLike = {
  device_id?: string;
  name?: string | null;
  hostname?: string | null;
  os?: string | null;
  ip_address?: string | null;
  ip?: string | null;
  device_metadata?: Record<string, unknown> | null;
  current_user?: string | null;
  [key: string]: unknown;
};

function meta(device?: DeviceLike | null): Record<string, unknown> {
  const m = device?.device_metadata;
  return m && typeof m === 'object' ? (m as Record<string, unknown>) : {};
}

function asStr(v: unknown): string {
  if (v == null) return '';
  const s = String(v).trim();
  return s;
}

/** Hostname / friendly device name (never prefer UUID). */
export function getDeviceHostname(device?: DeviceLike | null, fallbackId?: string): string {
  if (!device && !fallbackId) return '—';
  const m = meta(device);
  const candidates = [
    device?.hostname,
    m.hostname,
    m.Hostname,
    m.computer_name,
    m.ComputerName,
    device?.name,
    m.device_name,
    m.name,
  ]
    .map(asStr)
    .filter(Boolean);

  for (const c of candidates) {
    // Skip obvious UUID-looking names
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(c)) continue;
    return c;
  }
  const id = asStr(device?.device_id || fallbackId);
  return id || '—';
}

/** Currently logged-on / last logged-on user from metadata. */
export function getDeviceLoggedOnUser(device?: DeviceLike | null): string {
  if (!device) return '';
  const m = meta(device);
  const candidates = [
    device?.current_user,
    m.current_user,
    m.CurrentUser,
    m.logged_in_user,
    m.logged_on_user,
    m.last_logged_on_user,
    m.LastLoggedOnUser,
    m.user,
    m.username,
    m.Username,
  ]
    .map(asStr)
    .filter(Boolean);

  const hostname = getDeviceHostname(device).toUpperCase();
  for (const c of candidates) {
    if (!c || c.toUpperCase() === 'UNKNOWN' || c === 'N/A') continue;
    // Avoid serial-like tokens mistaken for users
    if (/^[A-Z0-9]{8,20}$/.test(c) && !c.includes('\\') && !c.includes('.') && !c.includes('@')) {
      continue;
    }
    if (c.toUpperCase() === hostname) continue;
    return c;
  }
  return '';
}

/** Single-line label: "HOSTNAME · user" or hostname alone. */
export function formatDeviceLabel(device?: DeviceLike | null, fallbackId?: string): string {
  const host = getDeviceHostname(device, fallbackId);
  const user = getDeviceLoggedOnUser(device);
  if (user) return `${host} · ${user}`;
  return host;
}

/** Two-line identity for table cells. */
export function getDeviceIdentityLines(
  device?: DeviceLike | null,
  fallbackId?: string
): { primary: string; secondary: string } {
  return {
    primary: getDeviceHostname(device, fallbackId),
    secondary: getDeviceLoggedOnUser(device),
  };
}
